/**
 * PageContextSync — keeps server-side page_context_state in sync with
 * window.__pageContext so the AI knows what the user is looking at
 * BETWEEN chat turns (FB-MOX8LBC8).
 *
 * Mounts once in Layout. Polls window.__pageContext every 3 seconds.
 * When the snapshot's focused_entity / page_path / page_kind changes,
 * fires a fire-and-forget upsert to /api/v1/workspaces/:wsId/page-context.
 *
 * The chat handler reads the row at the start of each turn and
 * threads "the user has been looking at X" into the system prompt
 * context — pronouns ("this person", "him", "this bill") resolve
 * cleanly without the user needing to re-state.
 */

import { useEffect, useRef } from "react";
import { getCsrfHeader } from "../lib/csrf";
import { extractRenderSnapshot, serializeSnapshot } from "../lib/render-snapshot";

const POLL_INTERVAL_MS = 3000;

interface SyncedSnapshot {
  page_kind: string | null;
  page_path: string | null;
  focused_entity_type: string | null;
  focused_entity_id: string | null;
  focused_entity_label: string | null;
  visible_summary: string | null;
}

/**
 * Extract a plain-text summary of what's actually rendered on the
 * screen — headings + card titles + any AI-composed panel — so the
 * chat handler (and external MCP clients via get_active_page_context)
 * can SEE what's on the user's screen, not just guess from the URL.
 *
 * Scrapes the DOM rather than expecting pages to register a richer
 * window.__pageContext.visible. Works for every page out of the box;
 * the per-page visible registry is the override path when pages want
 * to inject structured state too.
 *
 * Output shape: ' | '-separated list of headings and card titles,
 * truncated to ~1800 chars. Examples:
 *   "Rep. Tom McClintock — Weekly Brief | THIS WEEK | Sponsored Bills | Fast Followers"
 *   "Dashboard | While you were away | 3 AI compositions ready | Daily Brief — May 17"
 */
function extractRenderedSummary(): string | null {
  if (typeof document === "undefined") return null;
  try {
    // Prefer the main content area; fall back to body. The Layout
    // component renders content into <main> via React Router outlet.
    const root: Element =
      document.querySelector("main") ??
      document.querySelector("[role='main']") ??
      document.body;

    // Pull headings (h1-h4) in document order — that's the rendered
    // structure. Limit to first 20 to bound payload size.
    const HEADING_LIMIT = 20;
    const headings = Array.from(root.querySelectorAll("h1, h2, h3, h4"))
      .slice(0, HEADING_LIMIT)
      .map((el) => (el.textContent ?? "").trim())
      .filter((s) => s.length > 0 && s.length < 200);

    // Pull labeled "card title" elements — common pattern: cards have
    // a font-semibold/font-medium element near the top. We grep for
    // [data-card-title] (preferred — when pages opt in) but fall back
    // to .text-sm.font-semibold / .text-base.font-semibold which is
    // the Tailwind shape most card components use.
    const CARD_LIMIT = 12;
    const cardTitles = Array.from(
      root.querySelectorAll(
        "[data-card-title], .card-title, h5.font-semibold"
      )
    )
      .slice(0, CARD_LIMIT)
      .map((el) => (el.textContent ?? "").trim())
      .filter((s) => s.length > 0 && s.length < 120);

    // Dedupe while preserving order — headings often appear inside
    // cards too, no point repeating.
    const seen = new Set<string>();
    const ordered: string[] = [];
    for (const s of [...headings, ...cardTitles]) {
      const k = s.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      ordered.push(s);
    }

    if (ordered.length === 0) return null;

    let summary = ordered.join(" | ");
    if (summary.length > 1800) summary = summary.slice(0, 1797) + "...";
    return summary;
  } catch {
    return null;
  }
}

function readWindowSnapshot(): SyncedSnapshot {
  const ctx = (typeof window !== "undefined" ? (window as any).__pageContext : null) as
    | { kind?: string; focused_entity?: unknown; visible?: unknown }
    | null
    | undefined;
  let focused: { type?: string; id?: string; name?: string } | null = null;
  if (ctx?.focused_entity) {
    const fe = typeof ctx.focused_entity === "function" ? (ctx.focused_entity as any)() : ctx.focused_entity;
    if (fe && typeof fe === "object") focused = fe;
  }

  // First try the DOM extractor — that captures actual rendered content,
  // including AI-composed panels. Fall back to a structural summary of
  // window.__pageContext.visible when the DOM yields nothing (e.g. very
  // early in mount before content paints).
  let visibleSummary: string | null = extractRenderedSummary();
  if (!visibleSummary && ctx?.visible) {
    const v = typeof ctx.visible === "function" ? (ctx.visible as any)() : ctx.visible;
    if (v && typeof v === "object") {
      try {
        const keys = Object.keys(v).slice(0, 6);
        visibleSummary = keys.length > 0 ? keys.join(", ") : null;
      } catch { /* swallow */ }
    }
  }

  return {
    page_kind: ctx?.kind ?? null,
    page_path: typeof window !== "undefined" ? window.location.pathname : null,
    focused_entity_type: focused?.type ?? null,
    focused_entity_id: focused?.id ?? null,
    focused_entity_label: focused?.name ?? null,
    visible_summary: visibleSummary,
  };
}

function snapshotsEqual(a: SyncedSnapshot, b: SyncedSnapshot): boolean {
  return (
    a.page_kind === b.page_kind &&
    a.page_path === b.page_path &&
    a.focused_entity_type === b.focused_entity_type &&
    a.focused_entity_id === b.focused_entity_id &&
    a.focused_entity_label === b.focused_entity_label &&
    a.visible_summary === b.visible_summary
  );
}

export function PageContextSync({ workspaceId }: { workspaceId: string | null | undefined }) {
  const lastSentRef = useRef<SyncedSnapshot | null>(null);
  // Throttle screenshots — even on navigation, don't fire more than
  // once every 5s. PageContextSync polls every 3s; a fast click-through
  // could otherwise fire 10+ html2canvas captures back-to-back.
  const lastScreenshotAtRef = useRef<number>(0);
  const SCREENSHOT_MIN_INTERVAL_MS = 5_000;
  // Heartbeat — even when nothing has changed on the page, send a
  // page-context POST every 30s so the server's updated_at refreshes.
  // Without this the AI saw page_context as "STALE" when the user was
  // just sitting on a page (FB-MPB...): data was current, the
  // timestamp was just old because PageContextSync skipped the POST.
  // Heartbeats omit the screenshot — visual cache only refreshes on
  // real change or on chat send.
  const lastSentAtRef = useRef<number>(0);
  const HEARTBEAT_INTERVAL_MS = 30_000;

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      const snap = readWindowSnapshot();
      const isUnchanged =
        lastSentRef.current && snapshotsEqual(lastSentRef.current, snap);
      const dueForHeartbeat =
        Date.now() - lastSentAtRef.current >= HEARTBEAT_INTERVAL_MS;
      // Skip the POST when nothing changed AND it's not heartbeat time
      // yet. This keeps the previous "don't spam" behavior while still
      // refreshing updated_at every 30s so the AI sees the user as
      // "live on this page" rather than "stale context."
      if (isUnchanged && !dueForHeartbeat) return;
      // Don't waste a request when there's literally nothing to report.
      if (
        !snap.page_kind &&
        !snap.page_path &&
        !snap.focused_entity_id
      ) return;

      // Page CHANGED — that's a useful moment to refresh the AI's
      // visual cache. The chat-send screenshot covers user-driven
      // moments; this covers navigation moments. Together: the AI
      // sees the latest viewport regardless of whether the user
      // typed anything. Throttled to once per 5s + only when tab is
      // visible (capturing a backgrounded tab wastes CPU and ships
      // garbage).
      const isPageChange =
        !lastSentRef.current ||
        lastSentRef.current.page_path !== snap.page_path ||
        lastSentRef.current.focused_entity_id !== snap.focused_entity_id;
      const tabVisible =
        typeof document !== "undefined" && document.visibilityState !== "hidden";
      const throttleOk =
        Date.now() - lastScreenshotAtRef.current >= SCREENSHOT_MIN_INTERVAL_MS;
      const shouldCaptureScreenshot = isPageChange && tabVisible && throttleOk;

      lastSentRef.current = snap;
      let screenshotPayload:
        | { data: string; media_type: "image/jpeg" | "image/png" | "image/webp" }
        | undefined;
      if (shouldCaptureScreenshot) {
        try {
          const { captureViewportScreenshot } = await import("../lib/screenshot");
          if (cancelled) return;
          const shot = await captureViewportScreenshot();
          if (cancelled) return;
          if (shot) {
            screenshotPayload = { data: shot.data, media_type: shot.media_type };
            lastScreenshotAtRef.current = Date.now();
          }
        } catch { /* swallow — page-context POST still goes through */ }
      }

      // Hiding the workspace or navigating can finish while capture awaits.
      // Never upload a late screenshot/context from the prior effect.
      if (cancelled) return;

      // Structured render snapshot — cheap DOM-to-JSON of what's on
      // screen. Captured on every POST (every change AND every 30s
      // heartbeat) since it's ~10-20ms, vs html2canvas's 200-500ms.
      // The AI's primary view-awareness signal; screenshots are
      // fallback for things text can't capture.
      let renderJson: string | undefined;
      try {
        const renderSnap = extractRenderSnapshot();
        if (renderSnap) renderJson = serializeSnapshot(renderSnap);
      } catch { /* swallow — page-context POST still goes through */ }

      try {
        await fetch(
          `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/page-context`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json", ...getCsrfHeader() },
            body: JSON.stringify({
              ...snap,
              screenshot: screenshotPayload,
              render_snapshot: renderJson,
            }),
          }
        );
        if (!cancelled) lastSentAtRef.current = Date.now();
      } catch { /* fire-and-forget — next tick will retry */ }
    };
    // Fire once on mount, then poll.
    void tick();
    const id = setInterval(() => void tick(), POLL_INTERVAL_MS);

    // AI-driven on-demand screenshot capture. The take_screenshot MCP
    // choreography tool broadcasts cmcp:take-screenshot; we capture +
    // upload immediately, bypassing the navigation-change gate. Resets
    // the throttle so the on-demand capture always lands.
    const onTakeScreenshot = async () => {
      if (cancelled) return;
      try {
        const { captureViewportScreenshot } = await import("../lib/screenshot");
        if (cancelled) return;
        const shot = await captureViewportScreenshot();
        if (cancelled || !shot) return;
        const snap = readWindowSnapshot();
        lastScreenshotAtRef.current = Date.now();
        await fetch(
          `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/page-context`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json", ...getCsrfHeader() },
            body: JSON.stringify({
              ...snap,
              screenshot: { data: shot.data, media_type: shot.media_type },
            }),
          }
        );
      } catch { /* swallow */ }
    };
    window.addEventListener("cmcp:take-screenshot", onTakeScreenshot);

    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("cmcp:take-screenshot", onTakeScreenshot);
    };
  }, [workspaceId]);

  return null;
}
